import type { MarketingFunnel as Funnel } from '@/lib/api/marketing/marketing-dashboard-api'
import { MdHelpOutline } from 'react-icons/md'
import DeltaBadge from './delta-badge'
import { deltaOf, formatNumber, formatPercent } from './marketing-format'

interface MarketingFunnelProps {
    current: Funnel
    previous: Funnel | null
    onHelp?: () => void
}

/**
 * `nested`: a etapa é subconjunto da anterior (só ganha quem tem oportunidade). A
 * compra NÃO é: o agendamento direto vende sem fechar oportunidade no funil, então
 * "Compraram" pode passar de "Oportunidade ganha" e só se mede contra os leads.
 */
const STEPS: Array<{ key: keyof Funnel; label: string; hint: string; nested: boolean }> = [
    { key: 'leads', label: 'Leads', hint: 'Criados no período', nested: false },
    { key: 'withDeal', label: 'Com oportunidade', hint: 'Abriram oportunidade (em qualquer data)', nested: true },
    { key: 'won', label: 'Oportunidade ganha', hint: 'Fecharam uma oportunidade', nested: true },
    { key: 'bought', label: 'Compraram', hint: 'Até hoje, com ou sem oportunidade', nested: false },
]

const BAR_COLOR = '#2a78d6'

/**
 * Funil da COORTE: os leads que chegaram no período e até onde cada um foi. Contado
 * por lead — o lead com três oportunidades conta uma vez.
 */
export default function MarketingFunnel({ current, previous, onHelp }: MarketingFunnelProps) {
    const top = Math.max(current.leads, 1)

    return (
        <div className="bg-base-100 rounded-xl border border-base-300/50 p-5 h-full">
            <div className="flex items-start justify-between gap-2 mb-4">
                <div>
                    <h3 className="text-lg font-semibold text-base-content">Funil dos leads do período</h3>
                    <p className="text-sm text-base-content/60">Leads de aquisição, contados por lead</p>
                </div>
                {onHelp && (
                    <button
                        type="button"
                        className="btn btn-ghost btn-xs btn-circle text-base-content/40 hover:text-base-content"
                        onClick={onHelp}
                        aria-label="Como o funil é calculado?"
                        title="Como o funil é calculado?"
                    >
                        <MdHelpOutline className="text-base" />
                    </button>
                )}
            </div>

            <div className="flex flex-col gap-4">
                {STEPS.map((step, index) => {
                    const value = current[step.key]
                    const prevValue = previous ? previous[step.key] : null
                    const prevStep = index > 0 ? current[STEPS[index - 1].key] : null
                    const stepRate = prevStep ? (value / prevStep) * 100 : null
                    const overallRate = index > 0 && current.leads ? (value / current.leads) * 100 : null
                    const prevOverall =
                        index > 0 && previous?.leads ? (previous[step.key] / previous.leads) * 100 : null
                    const width = Math.max((value / top) * 100, value > 0 ? 2 : 0)

                    return (
                        <div key={step.key}>
                            <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
                                <div className="flex items-baseline gap-2">
                                    <span className="text-sm font-medium text-base-content">{step.label}</span>
                                    <span className="text-[11px] text-base-content/50">{step.hint}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-bold">{formatNumber(value)}</span>
                                    {previous && (
                                        <DeltaBadge
                                            delta={deltaOf(value, prevValue)}
                                            title={`Antes: ${formatNumber(prevValue ?? 0)}`}
                                        />
                                    )}
                                </div>
                            </div>
                            <div className="h-3 w-full rounded bg-base-200 overflow-hidden">
                                <div
                                    className="h-full rounded"
                                    style={{ width: `${width}%`, backgroundColor: BAR_COLOR }}
                                />
                            </div>
                            {index > 0 && (
                                <p className="mt-1 text-[11px] text-base-content/60">
                                    {step.nested && <>{formatPercent(stepRate)} da etapa anterior · </>}
                                    {formatPercent(overallRate)} dos leads
                                    {previous && prevOverall != null && <> (antes: {formatPercent(prevOverall)})</>}
                                </p>
                            )}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
